import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { api } from '../../api/endpoints'
import type { ManagedInfo, PatchChannelBody } from '../../api/types'
import { Avatar } from '../../components/Avatar'
import { BotTag } from '../../components/BotTag'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { Dialog } from '../../components/Dialog'
import { Icon } from '../../components/Icon'
import { botAcceptsMessages, botConversationNote, botDisplayName, findBot } from '../../lib/bots'
import { useBots } from '../../state/bots'
import { channelDisplayName, dmPartnerId } from '../../lib/channels'
import { channelSettingsAccess, managedInfo, policyLabels } from '../../lib/managed'
import { toast } from '../../state/toasts'
import { syncAll } from '../../sync/syncEngine'
import { useConversation } from '../conversation/ConversationContext'
import { toastChannelError } from './channelErrors'
import { EditChannelForm, type ChannelNameAndTopic } from './EditChannelForm'
import { leaveConversation, LeaveConfirmDialog, useLeaveAccess } from './leave'
import { ManagedBadge } from './ManagedBadge'
import { MembersSection } from './MembersSection'
import { NotificationSection } from './NotificationSection'
import { PostingSection } from './PostingSection'
import { MediaSection } from './MediaSection'

type Confirm = 'leave' | 'archive' | 'unarchive'

/** A sysadmin's change to a managed channel, saved with ?override=true once confirmed. */
interface OverrideChange {
  body: PatchChannelBody
  success: string
  onSaved?: () => void
}

export function ChannelDetailsDrawer({ open, onClose, focus }: { open: boolean; onClose: () => void; focus?: 'notifications' }) {
  const { channel, users, me, canModerate } = useConversation()
  const navigate = useNavigate()
  const [editing, setEditing] = useState(false)
  const [confirm, setConfirm] = useState<Confirm | null>(null)
  const [override, setOverride] = useState<OverrideChange | null>(null)
  const name = channelDisplayName(channel, users, me.id)
  const isChannel = channel.type === 'channel'
  const bot = channel.type === 'bot_dm'
  const partnerId = dmPartnerId(channel, me.id, users)
  const accepts = bot && botAcceptsMessages(channel)
  const { list: bots } = useBots(Boolean(accepts))
  const botInfo = accepts ? findBot(bots, partnerId) : undefined
  const botDescription = bot && partnerId ? (users.get(partnerId)?.description ?? botInfo?.description ?? null) : null
  // Channels and group chats can be managed; direct and bot conversations never are.
  const managed = managedInfo(channel)
  const noun = isChannel ? 'channel' : 'group chat'
  // Name, topic, archive and posting: owners and admins, except that the integration controls a managed channel
  // (sysadmins may override).
  const settings = channelSettingsAccess(channel, isChannel && canModerate, me.role)
  const isAdmin = me.role === 'admin' || me.role === 'sysadmin'
  // Nobody leaves a DM or a bot conversation; a managed member can't leave (the integration decides).
  const leave = useLeaveAccess(channel, me.id)

  const leaveChannel = async () => {
    if (!(await leaveConversation(channel.id, me.id, name))) return
    onClose()
    navigate('/')
  }

  const patch = async (body: PatchChannelBody, success: string): Promise<boolean> => {
    try {
      await api.patchChannel(channel.id, body, { override: settings.override })
      await syncAll()
      toast(success)
      return true
    } catch (error) {
      toastChannelError("Couldn't update the channel.", error)
      return false
    }
  }

  const setArchived = (archived: boolean) => patch({ archived }, archived ? 'Channel archived' : 'Channel restored')

  const savePosting = async (body: PatchChannelBody) => {
    if (settings.override) setOverride({ body, success: 'Posting settings saved' })
    else await patch(body, 'Posting settings saved')
  }

  const submitNameAndTopic = (values: ChannelNameAndTopic) => setOverride({ body: values, success: 'Channel updated', onSaved: () => setEditing(false) })

  return (
    <>
      <Dialog open={open} onClose={onClose} title={isChannel ? 'Channel details' : 'Conversation details'} titleId="details-title" className="drawer" closeLabel="Close details">
        <div className="drawer-content">
          <div className="group-summary">
            {channel.type === 'dm' || bot ? (
              <Avatar user={partnerId ? users.get(partnerId) : undefined} id={partnerId ?? channel.id} name={name} />
            ) : (
              <Avatar name={name} id={channel.id} variant={isChannel ? 'channel' : 'person'} />
            )}
            {editing ? (
              <EditChannelForm onDone={() => setEditing(false)} onSubmitOverride={settings.override ? submitNameAndTopic : undefined} />
            ) : (
              <>
                <h2>{name}</h2>
                {bot ? <BotTag className="summary-bot-tag" /> : null}
                {channel.topic ? <p>{channel.topic}</p> : null}
                {settings.canEdit && channel.isMember ? (
                  <button type="button" className="text-button" onClick={() => setEditing(true)}>
                    {settings.override ? 'Edit name and topic (override)' : 'Edit name and topic'}
                  </button>
                ) : null}
              </>
            )}
            {managed ? (
              <div className="managed-details">
                <ManagedBadge managed={managed} />
                <p className="policy-notice" role="note">
                  <Icon name="idCard" />
                  <span>Membership of this {noun} is managed automatically. Ask an admin if it looks wrong.</span>
                </p>
                {isAdmin ? (
                  <p className="managed-key">
                    External key <code>{managed.externalKey}</code> · {policyLabels[managed.policy] ?? managed.policy}
                  </p>
                ) : null}
              </div>
            ) : null}
            {bot && botDescription ? <p>{botDescription}</p> : null}
            {bot ? (
              <p className="policy-notice" role="note">
                <Icon name={accepts ? 'send' : 'message'} />
                <span>
                  {accepts
                    ? `Messages you send here are passed to ${botInfo?.webhookHost ?? `${botDisplayName(channel, users, me.id)}'s service`}.`
                    : botConversationNote(botDisplayName(channel, users, me.id))}
                </span>
              </p>
            ) : null}
            {/* Every conversation here belongs to the company, not to a personal account: administrators can read it,
                which the rules restrict to them and write to the audit log. */}
            <p className="policy-notice" role="note">
              <Icon name="shield" />
              Part of your company workspace
            </p>
          </div>

          {channel.isMember ? <NotificationSection focus={focus === 'notifications'} /> : null}
          <section className="drawer-section drawer-rows" aria-label="In this conversation">
            <Link className="drawer-row" to={`/search?channelId=${encodeURIComponent(channel.id)}`}>
              <Icon name="search" />
              <span>Search in this conversation</span>
              <Icon name="chevronRight" />
            </Link>
            <MediaSection />
          </section>
          {isChannel ? <PostingSection canEdit={settings.canEdit && channel.isMember} onSave={savePosting} /> : null}
          {/* A direct message is the two of you: no list of members, as for a bot. */}
          {bot || channel.type === 'dm' ? null : <MembersSection />}
        </div>

        {channel.isMember && (settings.canEdit || leave !== 'none') ? (
          <div className="drawer-footer">
            {settings.canEdit ? (
              <button
                type="button"
                className="leave-group"
                onClick={() => {
                  if (!channel.archivedAt) setConfirm('archive')
                  else if (settings.override) setConfirm('unarchive')
                  else void setArchived(false)
                }}
              >
                <Icon name="archive" />
                {channel.archivedAt ? 'Unarchive channel' : 'Archive channel'}
                {settings.override ? ' (override)' : ''}
              </button>
            ) : null}
            {leave === 'allowed' ? (
              <button type="button" className="leave-group" onClick={() => setConfirm('leave')}>
                <Icon name="leave" />
                {isChannel ? 'Leave channel' : 'Leave group'}
              </button>
            ) : null}
            {leave === 'managed' && managed ? (
              <p className="drawer-note" role="note">
                <Icon name="lock" />
                <span>You can't leave this {noun}: your membership is managed automatically.</span>
              </p>
            ) : null}
          </div>
        ) : null}
      </Dialog>

      <LeaveConfirmDialog open={confirm === 'leave'} name={name} onClose={() => setConfirm(null)} onConfirm={leaveChannel} />
      <ConfirmDialog
        open={confirm === 'archive'}
        title={`Archive ${name}?`}
        confirmLabel={settings.override ? 'Archive anyway' : 'Archive'}
        onClose={() => setConfirm(null)}
        onConfirm={async () => {
          await setArchived(true)
        }}
      >
        <p>Archived channels are read-only and hidden from the conversation list. You can unarchive it later.</p>
        {settings.override && managed ? <OverrideWarning name={name} managed={managed} /> : null}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm === 'unarchive'}
        title={`Unarchive ${name}?`}
        confirmLabel="Unarchive anyway"
        onClose={() => setConfirm(null)}
        onConfirm={async () => {
          await setArchived(false)
        }}
      >
        {managed ? <OverrideWarning name={name} managed={managed} /> : null}
      </ConfirmDialog>
      <ConfirmDialog
        open={Boolean(override)}
        title="Override managed settings?"
        confirmLabel="Save anyway"
        onClose={() => setOverride(null)}
        onConfirm={async () => {
          if (override && (await patch(override.body, override.success))) override.onSaved?.()
        }}
      >
        {managed ? <OverrideWarning name={name} managed={managed} /> : null}
      </ConfirmDialog>
    </>
  )
}

function OverrideWarning({ name, managed }: { name: string; managed: ManagedInfo }) {
  return (
    <p>
      <strong>{name} is managed by {managed.integrationName}.</strong> The next sync may undo this change. Overrides are recorded in the audit log.
    </p>
  )
}
